import * as React from 'react'

export type CountUpProps = {
  /** The final value. Rendered formatted on the server, then counted up to on mount. */
  value: number
  /** Formats every intermediate frame as well as the final value. */
  format: (value: number) => string
  /** Appended after the digits, outside the animation (e.g. ` €`, `%`). */
  suffix?: string
  className?: string
}

const DURATION_MS = 1_100

/** Ease-out: fast start, settling finish, so the final value reads as arriving rather than stopping. */
function easeOut(progress: number): number {
  return 1 - (1 - progress) ** 3
}

/**
 * A figure that counts up once, on mount.
 *
 * The server renders the FINAL value, so the prerendered HTML is correct for crawlers and no-JS
 * visitors and hydration matches. The animation then replays it from zero — and is skipped
 * entirely when the visitor asks for reduced motion, which leaves the final value on screen.
 */
export function CountUp({ value, format, suffix = '', className }: CountUpProps): React.ReactElement {
  const [shown, setShown] = React.useState(value)
  const hasRamped = React.useRef(false)

  React.useEffect(() => {
    // Once only, as the name says. A later `value` change is the client refetch landing a fresher
    // figure, not a new mount: commit it straight away, because replaying the ramp would snap every
    // figure on the page back to zero seconds after the first one finished. Reduced motion takes
    // the same path — the value still has to be committed, or the page would stay pinned to the
    // build-time snapshot.
    if (hasRamped.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hasRamped.current = true
      setShown(value)
      return
    }

    hasRamped.current = true

    let frame = 0
    const start = performance.now()

    function step(now: number): void {
      const progress = Math.min((now - start) / DURATION_MS, 1)
      setShown(Math.round(easeOut(progress) * value))
      if (progress < 1) frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [value])

  return (
    <span className={className}>
      {format(shown)}
      {suffix}
    </span>
  )
}
